import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import SearchInput from './components/SearchInput';
import SearchResults from './components/SearchResults';
import RecommendedQuestions from './components/RecommendedQuestions';
import ConversationHistory from './components/ConversationHistory';
import Support from './components/Support';
import api from './services/api';

function App() {
  const [sessionId, setSessionId] = useState(null);
  const [status, setStatus] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [backendUnavailable, setBackendUnavailable] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Initialize session and fetch status
    const initialize = async () => {
      try {
        const newSessionResponse = await api.createNewSession();
        const newSessionId = newSessionResponse.session_id;
        setSessionId(newSessionId);

        const statusResponse = await api.getStatus(newSessionId);
        setStatus(statusResponse);
        setBackendUnavailable(false);
      } catch (err) {
        console.error('Failed to initialize:', err);
        setBackendUnavailable(true);
        setError('Failed to connect to backend. Please try again later.');
      }
    };

    initialize();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSearch = async (query) => {
    if (!query.trim() || !sessionId) return;

    setLoading(true);
    setError(null);

    // Add user message
    const userMessage = { role: 'user', content: query };
    setMessages(prev => [...prev, userMessage]);

    try {
      const results = await api.search(query, sessionId, {
        use_enhanced_features: true,
        use_query_improvement: true,
        use_context_parsing: true
      });

      // Add assistant message with results
      const assistantMessage = {
        role: 'assistant',
        content: results.ai_response || '',
        results: results,
        query: query
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      console.error('Search failed:', err);
      setError('Search failed. Please try again.');
      const errorMessage = {
        role: 'assistant',
        content: 'Search failed. Please try again.',
        error: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleNewSession = async () => {
    try {
      const response = await api.createNewSession();
      setSessionId(response.session_id);
      setMessages([]);
      setError(null);

      // Refresh status
      const statusResponse = await api.getStatus(response.session_id);
      setStatus(statusResponse);
    } catch (err) {
      console.error('Failed to create new session:', err);
      setError('Failed to create new session.');
    }
  };

  // Show normal UI unless we've confirmed backend is unavailable
  const isIndexed = backendUnavailable ? false : (status?.indexed !== false);

  return (
    <div className="App">
      <div className="background-overlay" style={{ backgroundImage: 'url(/quran.png)' }}></div>
      <div className="container">
        <header className="header">
          <div className="header-left">
            <button
              className="hamburger-btn"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Toggle conversation history"
            >
              <span className="hamburger-icon">☰</span>
            </button>
            <div className="app-branding">
              <h1 className="app-title">Tafseel Search</h1>
              <p className="app-subtitle">AI powered search for Tafseer texts!</p>
            </div>
          </div>
          <div className="header-actions">
            {messages.length > 0 && (
              <button className="new-session-btn" onClick={handleNewSession}>
                New Session 
              </button>
            )}
          </div>
        </header>

        <div className="main-content">
          <div className={`content-wrapper ${messages.length > 0 ? 'has-messages' : ''}`}>
            {!isIndexed ? (
              <div className="welcome-section">
                <h2 className="welcome-greeting">As-salamu alaykum</h2>
                <h3 className="welcome-question">What would you like to ask?</h3>
                <div className="warning-message">
                  Search functionality is currently unavailable. Please check back later.
                </div>
              </div>
            ) : (
              <>
                {messages.length === 0 && (
                  <div className="welcome-section">
                    <h2 className="welcome-greeting">As-salamu alaykum</h2>
                    <h3 className="welcome-question">What would you like to ask?</h3>
                    <RecommendedQuestions onQuestionClick={handleSearch} />
                  </div>
                )}

                {messages.length > 0 && (
                  <>
                    <div className="messages-container">
                      {messages.map((message, index) => {
                        const isLatestMessage = index === messages.length - 1;
                        return (
                          <div key={index} className={`message ${message.role}`}>
                            {message.role === 'user' ? (
                              <div className="user-message">
                                <div className="message-content">{message.content}</div>
                              </div>
                            ) : (
                              <div className="assistant-message">
                                {message.error ? (
                                  <div className="error-message">{message.content}</div>
                                ) : (
                                  <SearchResults
                                    results={message.results}
                                    enableTyping={isLatestMessage && !loading}
                                    sessionId={sessionId}
                                    messageIndex={index}
                                    query={message.query}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {loading && (
                        <div className="message assistant">
                          <div className="loading-indicator">
                            <div className="spinner"></div>
                            <span>Searching through Tafseer texts...</span>
                          </div>
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          {/* Fixed Search Input at Bottom */}
          {isIndexed && (
            <div className="search-input-fixed">
              <SearchInput onSearch={handleSearch} disabled={loading || !isIndexed} />
            </div>
          )}

          {/* Overlay for mobile sidebar */}
          {sidebarOpen && (
            <div
              className="sidebar-overlay"
              onClick={() => setSidebarOpen(false)}
            />
          )}

          {/* Sidebar */}
          <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
            <button
              className="sidebar-close-btn"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close conversation history"
            >
              ×
            </button>
            <ConversationHistory messages={messages} />
          </aside>
        </div>

        {/* Footer with Support */}
        {messages.length === 0 && (
          <footer className="footer">
            <Support isPopup={false} />
            <p className="footer-disclaimer">
              This AI search is in Beta and may produce incorrect information. Please help us improve by{' '}
              <a
                href="https://docs.google.com/forms/d/e/1FAIpQLSc9RGZ_xtC3UE-ou6TCMJQ8we3ynjeoNkUzz1LJXZuLcbEsaQ/viewform?usp=dialog"
                target="_blank"
                rel="noopener noreferrer"
              >
                submitting feedback
              </a>
              .
            </p>
            <p className="footer-mobile-note">
              This app is optimized for mobile devices. For the best experience, please use a mobile phone or tablet.
            </p>
          </footer>
        )}

        {error && (
          <div className="error-toast">
            {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
